"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import {
  MAIN_SIDENAV_WORKSPACE_SECTIONS,
  sectionHref,
  useAllowedSections,
} from "@/components/Layouts/pageSections";
import { ISectionNavItem, SectionSideNav } from "./SectionSideNav";

// A workspace's sections: the current one's (Manage workspace) or, given an
// id, one opened from Settings › Workspaces, which goes back there.
export const ManageWorkspaceSideNav = ({
  workspaceId,
}: {
  workspaceId?: string;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { spotGroup } = useSpotUser();
  const isCurrent = !workspaceId;
  const groupId = workspaceId ?? spotGroup.id;
  const { data: groupData } = useRemoteData(
    DATA_KEYS.GROUP,
    { path: { group_id: groupId } },
    { enabled: !isCurrent }
  );
  // The current workspace's Analytics and Connections sit in the main
  // sidenav; one opened from Settings lists them here.
  const visible = useAllowedSections("workspace", groupId).filter(
    (section) =>
      !isCurrent ||
      !MAIN_SIDENAV_WORKSPACE_SECTIONS.some((id) => id === section.id)
  );
  const tab = searchParams?.get("tab");
  const activeId = visible.some((section) => section.id === tab)
    ? tab
    : visible[0]?.id;

  const items: ISectionNavItem[] = visible.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
    isActive: section.id === activeId,
    href: sectionHref(pathname ?? "", section.id),
  }));

  return (
    <SectionSideNav
      title={isCurrent ? "Manage workspace" : (groupData?.group.name ?? "")}
      onBack={() =>
        router.push(isCurrent ? "/" : "/settings/administration/workspaces")
      }
      testIdPrefix="manage-workspace"
      groups={[{ items }]}
    />
  );
};
